/**
 * apiClient — Cliente HTTP Centralizado con Auto-Refresh de Sesión
 *
 * ¿Por qué existe?
 * ────────────────
 * El Access Token (JWT) dura solo 15 minutos. Este wrapper intercepta
 * cualquier respuesta 401 y llama silenciosamente a POST /auth/refresh
 * para obtener un nuevo par de tokens y reintentar. El usuario nunca
 * percibe la renovación.
 *
 * Si el refresh falla, NO navega: un cliente HTTP no decide a dónde va la
 * pantalla. Antes redirigía a /login, y como el proxy y otras pestañas
 * renuevan en paralelo, a veces echaba a alguien cuya sesión seguía viva.
 * Ahora devuelve una respuesta sintética que cada llamador trata como
 * cualquier error:
 *   - 401 `SESSION_EXPIRED`: la API rechazó el refresh (la sesión terminó de
 *     verdad). Además emite el aviso de `lib/sesion-expirada.ts`, una vez.
 *   - 503 `SESSION_REFRESH_UNAVAILABLE`: la API no pudo confirmarla (429, 5xx).
 *     Transitorio: las cookies siguen y el próximo intento puede andar.
 *
 * Uso:
 *   import { apiClient } from '@/lib/apiClient';
 *   const data = await apiClient('/auth/me');
 *   const resp = await apiClient('/some-endpoint', { method: 'POST', body: JSON.stringify({...}) });
 */

// Import relativo (no `@/`) a propósito: el runner de tests del web compila
// estos archivos sueltos y no resuelve alias.
import { emitirSesionExpirada } from './sesion-expirada';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

// Flag para evitar llamadas concurrentes al refresh
let isRefreshing = false;

interface PendingRequest {
	resolve: (value: Response) => void;
	reject: (reason: unknown) => void;
	input: string;
	init?: RequestInit;
}

// Cola de peticiones que esperan a que el refresh termine
let pendingQueue: PendingRequest[] = [];

/** Entrega la cola completa y la deja vacía. */
function takePendingQueue(): PendingRequest[] {
	const queue = pendingQueue;
	pendingQueue = [];
	return queue;
}

/** Refresh OK: reintenta cada petición encolada con las cookies ya renovadas. */
function retryPendingQueue() {
	for (const { resolve, reject, input, init } of takePendingQueue()) {
		fetch(input, { ...init, credentials: 'include' }).then(resolve, reject);
	}
}

/** Refresh fallido: cada petición encolada recibe SU PROPIA respuesta (un body se lee una sola vez). */
function answerPendingQueue(buildResponse: () => Response) {
	for (const { resolve } of takePendingQueue()) {
		resolve(buildResponse());
	}
}

/** Refresh sin respuesta (red): los encolados fallan con el mismo error, en vez de quedar colgados. */
function rejectPendingQueue(error: unknown) {
	for (const { reject } of takePendingQueue()) {
		reject(error);
	}
}

/** Todo lo que no sea GET/HEAD intenta cambiar algo. */
function isMutation(init?: RequestInit): boolean {
	const method = (init?.method ?? 'GET').toUpperCase();
	return method !== 'GET' && method !== 'HEAD';
}

function jsonResponse(status: number, body: Record<string, unknown>): Response {
	return new Response(JSON.stringify(body), {
		status,
		headers: { 'Content-Type': 'application/json' },
	});
}

/** Texto para una sesión que expiró de verdad. Es el `message` del 401 `SESSION_EXPIRED`. */
export const SESSION_EXPIRED_MESSAGE = 'Tu sesión expiró. Vuelve a iniciar sesión.';

function sessionExpiredResponse(): Response {
	return jsonResponse(401, {
		statusCode: 401,
		code: 'SESSION_EXPIRED',
		message: SESSION_EXPIRED_MESSAGE,
	});
}

function refreshUnavailableResponse(): Response {
	return jsonResponse(503, {
		statusCode: 503,
		code: 'SESSION_REFRESH_UNAVAILABLE',
		message: 'No pudimos confirmar tu sesión. Intenta de nuevo en unos segundos.',
	});
}

/**
 * Wrapper de fetch que auto-refresca la sesión ante un 401.
 *
 * @param path  Ruta relativa al API (ej. '/auth/me', '/publicaciones')
 * @param init  Opciones estándar de fetch (method, headers, body, etc.)
 * @returns     Promise<Response> — la respuesta original o la reintentada
 */
export async function apiClient(
	path: string,
	init?: RequestInit,
): Promise<Response> {
	const url = `${API_BASE}${path}`;

	const response = await fetch(url, {
		...init,
		credentials: 'include', // Siempre enviar cookies HttpOnly
	});

	// Si no es 401, devolver directamente
	if (response.status !== 401) {
		return response;
	}

	// ─── No interceptar el ciclo de vida de auth ─────────────────
	// En /auth/login, /auth/me, /auth/refresh, /auth/logout, etc., un 401
	// significa "no hay sesión", no "sesión expirada", y refrescar ahí
	// produciría un loop.
	// El onboarding cuelga de /auth pero SÍ requiere sesión: debe refrescar
	// como cualquier endpoint protegido. Sin esta excepción, quien se demoraba
	// más de 15 min en el wizard recibía un "Unauthorized" crudo al finalizar
	// (incidente del 2026-09-22).
	const isOnboarding = path === '/auth/onboarding' || path.startsWith('/auth/onboarding/');
	if (path.startsWith('/auth/') && !isOnboarding) {
		return response;
	}

	// ─── 401 Interceptado — Intentar Refresh ─────────────────────

	// Si ya estamos refrescando, encolar esta petición
	if (isRefreshing) {
		return new Promise<Response>((resolve, reject) => {
			pendingQueue.push({ resolve, reject, input: url, init });
		});
	}

	isRefreshing = true;

	try {
		let refreshResponse: Response;
		try {
			refreshResponse = await fetch(`${API_BASE}/auth/refresh`, {
				method: 'POST',
				credentials: 'include',
			});
		} catch (networkError) {
			// Sin respuesta de la API (red caída, API reiniciando): los encolados
			// fallan con el mismo error y el llamador original también.
			rejectPendingQueue(networkError);
			throw networkError;
		}

		if (refreshResponse.ok) {
			// Refresh exitoso → Las nuevas cookies ya fueron seteadas por el servidor
			retryPendingQueue();

			// Reintentar la petición original que gatilló el 401
			return fetch(url, {
				...init,
				credentials: 'include',
			});
		}

		if (refreshResponse.status === 401) {
			// La API rechazó el refresh: la sesión terminó de verdad. Se avisa UNA
			// vez, y se marca si algo de lo que falló intentaba guardar un cambio
			// (se calcula antes de vaciar la cola).
			const mutacion = isMutation(init) || pendingQueue.some((pending) => isMutation(pending.init));
			answerPendingQueue(sessionExpiredResponse);
			emitirSesionExpirada({ mutacion });
			return sessionExpiredResponse();
		}

		// 429, 5xx u otro: la API no pudo confirmar la sesión, pero tampoco la
		// negó. Transitorio: sin aviso, y las cookies siguen para el próximo intento.
		answerPendingQueue(refreshUnavailableResponse);
		return refreshUnavailableResponse();
	} finally {
		isRefreshing = false;
	}
}

/**
 * Error de los atajos `apiGet`/`apiPost`/`apiPatch`: el mismo mensaje de siempre,
 * más el `status`, para que quien lo atrape distinga una sesión vencida (401) de un
 * permiso denegado (403) o una falla cualquiera.
 */
export class ApiRequestError extends Error {
	constructor(
		message: string,
		readonly status: number,
	) {
		super(message);
		this.name = 'ApiRequestError';
	}
}

/**
 * ¿El error es una sesión vencida? Tras un refresh rechazado, `apiClient` responde
 * 401 `SESSION_EXPIRED`; un 401 NO es falta de permisos, y mostrarlo como tal confunde.
 */
export function isSessionExpiredError(error: unknown): boolean {
	return error instanceof ApiRequestError && error.status === 401;
}

/**
 * Atajo para GET con parsing JSON automático.
 * Lanza un `ApiRequestError` si la respuesta no es ok.
 */
export async function apiGet<T = unknown>(path: string): Promise<T> {
	const res = await apiClient(path);
	if (!res.ok) {
		throw new ApiRequestError(`GET ${path} failed with status ${res.status}`, res.status);
	}
	return res.json() as Promise<T>;
}

/**
 * Atajo para POST con JSON body y parsing automático.
 */
export async function apiPost<T = unknown>(
	path: string,
	body?: unknown,
): Promise<T> {
	const res = await apiClient(path, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: body ? JSON.stringify(body) : undefined,
	});
	if (!res.ok) {
		throw new ApiRequestError(`POST ${path} failed with status ${res.status}`, res.status);
	}
	return res.json() as Promise<T>;
}

/**
 * Atajo para PATCH con JSON body y parsing automático.
 */
export async function apiPatch<T = unknown>(
	path: string,
	body?: unknown,
): Promise<T> {
	const res = await apiClient(path, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: body ? JSON.stringify(body) : undefined,
	});
	if (!res.ok) {
		throw new ApiRequestError(`PATCH ${path} failed with status ${res.status}`, res.status);
	}
	return res.json() as Promise<T>;
}
